import React, { FC, useState, useEffect } from 'react';
import styles from './Toolbar.module.scss';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { StitchingManipulationRules } from '../../../../shared/Helpers';

type ToolbarProps = {
  selectAllClicked: () => void;
  rotateStart: () => void;
  rotateEnd: () => void;
  saveClicked: () => void;
  resetClicked: () => void;
  completeClicked: () => void;
  rejectSequenceClicked: () => void;
  onUndoClick: (shouldDo: boolean) => void;
  onRedoClick: (shouldDo: boolean) => void;
  isStitched: boolean;
  isRejected: boolean;
  isToolbarBusy: boolean;
  anyActive: boolean;
};

const Toolbar: FC<ToolbarProps> = ({
  selectAllClicked,
  rotateStart,
  rotateEnd,
  saveClicked,
  resetClicked,
  isStitched,
  isRejected,
  completeClicked,
  rejectSequenceClicked,
  isToolbarBusy,
  anyActive,
  onUndoClick,
  onRedoClick
}) => {
  const [isSaveItemBusy, setIsSaveItemBusy] = useState(false);
  const [isCompleteItemBusy, setIsCompleteItemBusy] = useState(false);

  const saveItemClicked = () => {
    setIsSaveItemBusy(true);
    saveClicked();
  };

  const completeItemClicked = () => {
    setIsCompleteItemBusy(true);
    completeClicked();
  };

  const rejectSequenceItemClicked = () => {
    setIsCompleteItemBusy(true);
    rejectSequenceClicked();
  };

  useEffect(() => {
    if (!isToolbarBusy) {
      setIsSaveItemBusy(false);
      setIsCompleteItemBusy(false);
    }
  }, [isToolbarBusy]);

  const isDisabled = !(isStitched || isToolbarBusy);

  return (
    <div className={styles.toolbarContainer}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarItem} title="Select All">
          <img
            className={classNames(styles.selectAllItem, {
              [styles.stitchingCompleted]: isStitched
            })}
            onClick={() => {
              isDisabled && selectAllClicked();
            }}
            alt=""
          />
        </div>
        <div className={styles.delimeter} />
        <div className={styles.toolbarItem} title="Rotate">
          <img
            className={classNames(styles.rotateItem, {
              [styles.stitchingCompleted]: isStitched || !anyActive
            })}
            onMouseDown={() => {
              isDisabled && anyActive && rotateStart();
            }}
            onMouseUp={() => {
              isDisabled && rotateEnd();
            }}
            alt=""
          />
        </div>
        <div className={styles.delimeter} />
        <div className={styles.toolbarItem} title="Undo">
          <img
            className={classNames(styles.undoItem, {
              [styles.stitchingCompleted]: isStitched
            })}
            onMouseDown={() => {
              isDisabled && onUndoClick(true);
            }}
            onMouseUp={() => {
              isDisabled && onUndoClick(false);
            }}
            alt=""
          />
        </div>
        <div className={styles.delimeter} />
        <div className={styles.toolbarItem} title="Redo">
          <img
            className={classNames(styles.redoItem, {
              [styles.stitchingCompleted]: isStitched
            })}
            onMouseDown={() => {
              isDisabled && onRedoClick(true);
            }}
            onMouseUp={() => {
              isDisabled && onRedoClick(false);
            }}
            alt=""
          />
        </div>
        <div className={styles.delimeter} />
        <div className={styles.toolbarItem} title="Save">
          {!isSaveItemBusy && (
            <img
              className={classNames(styles.saveItem, {
                [styles.stitchingCompleted]: isStitched
              })}
              onClick={() => {
                isDisabled && saveItemClicked();
              }}
              alt=""
            />
          )}
          {isSaveItemBusy && (
            <span className={styles.itemPlaceholder}>
              <FontAwesomeIcon icon={faSpinner} className={classNames(styles.rotate)} />
            </span>
          )}
        </div>
        <div className={styles.delimeter} />
        <div className={styles.toolbarItem} title="Reset">
          <img
            className={classNames(styles.resetItem, {
              [styles.stitchingCompleted]: isStitched
            })}
            onClick={() => {
              isDisabled && resetClicked();
            }}
            alt=""
          />
        </div>
        <div className={styles.delimeter} />
        <div className={styles.toolbarItem} title={isRejected ? 'Stitching rejected' : 'Reject'}>
          {isRejected ? (
            <img className={styles.rejectedSequence} alt="" />
          ) : (
            <img
              className={classNames(styles.rejectSequence, {
                [styles.stitchingCompleted]: isStitched
              })}
              onClick={() => {
                isDisabled && rejectSequenceItemClicked();
              }}
              alt=""
            />
          )}
        </div>
      </div>
      <div className={styles.toolbar}>
        <div className={styles.toolbarItem} title={isStitched ? 'Stitching completed' : 'Complete'}>
          {isStitched ? (
            <img className={styles.completedItem} alt="" />
          ) : (
            ((!isToolbarBusy || (isToolbarBusy && isSaveItemBusy)) && (
              <img
                className={styles.completeItem}
                onClick={() => {
                  isDisabled && completeItemClicked();
                }}
                alt=""
              />
            )) ||
            (isToolbarBusy && isCompleteItemBusy && (
              <span className={styles.itemPlaceholder}>
                <FontAwesomeIcon icon={faSpinner} className={classNames(styles.rotate)} />
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Toolbar;
